import type { CheckAddonOption, CheckOption, PlanFeatureOption } from '../types';

// Shown when /payments/check-options is unreachable, so a marketing page never
// renders an empty price list. Kept here rather than in a component so the
// landing CTA and the /intelligence one-pager quote identical prices. Kept in
// step with the admin Plans/Pricing screens so an outage never quotes a price
// the product no longer honours.
export const FALLBACK_CHECK_OPTIONS: CheckOption[] = [
    {
        id: 'standard',
        name: 'Standard',
        price: 218500,
        currency: 'NGN',
        popular: false,
        features: [
            'Personal Identification & Verification',
            '1 Guarantor verification',
            'Criminal History checks at police SCID',
            'Employee Physical Address Verification and intelligence gathering',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
    {
        id: 'enhanced',
        name: 'Premium',
        price: 400000,
        currency: 'NGN',
        popular: true,
        features: [
            'Personal Identification & Verification',
            'Criminal History checks at police SCID',
            'Previous Workplace Conduct (5 years work history)',
            'Social Media Reputation',
            'Employee Physical Address verification and intelligence gathering',
            '2 Guarantor verifications',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
    {
        id: 'pro',
        name: 'Professional',
        price: 900000,
        currency: 'NGN',
        popular: false,
        features: [
            'Personal Identification & Verification',
            'Criminal History checks at police SCID',
            'Financial & Credit',
            'Directorship History',
            'Credit history',
            'Previous Workplace Conduct',
            'Social Media Reputation',
            'Conflict Of Interest',
            'Professional Discipline Check (may require longer await time for report)',
            'Employee Physical Address verification and intelligence gathering',
            '2 Guarantor verifications',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
];

// The backend exposes an internal "Test (Live Payment Check)" plan — a live-gateway
// smoke test with a tiny charge, meant only for allow-listed accounts. It must never
// appear in the customer-facing pricing/picker, so filter it out everywhere.
export function isInternalTestOption(option: CheckOption): boolean {
    const id = (option.id ?? '').toLowerCase();
    const name = (option.name ?? '').toLowerCase();
    const features = (option.features ?? []).join(' ').toLowerCase();
    return (
        id === 'test' ||
        name.includes('live payment check') ||
        name.includes('smoke test') ||
        features.includes('smoke test') ||
        features.includes('allow-listed') ||
        features.includes('allow listed')
    );
}

// Drop internal/test-only plans from a list of check options.
export function visibleCheckOptions(options: CheckOption[]): CheckOption[] {
    return options.filter((option) => !isInternalTestOption(option));
}

// Deduped union of every medical test across the per-category panels. The API
// splits tests by investigation category; a marketing surface quoting a single
// price needs the flattened set.
export function medicalTestList(medical?: CheckOption['medicalAnalysis']): string[] {
    if (!medical?.tests) return [];
    const all = Object.values(medical.tests).flat().filter(Boolean) as string[];
    return Array.from(new Set(all));
}

// ==================== FEATURES AND ADD-ONS ====================

/**
 * The plan's priced features. These sum to the plan price, so the card can put
 * a figure against every bullet and let the customer pick individual lines.
 * Empty for a response that predates priced features.
 */
export function planFeatures(option: CheckOption): PlanFeatureOption[] {
    return option.featureDetails ?? [];
}

/** The add-ons a plan can be extended with, at face value. */
export function planAddons(option: CheckOption): CheckAddonOption[] {
    if (option.addons?.length) return option.addons;

    // Older responses only described the medical add-on, so synthesise it.
    const medical = option.medicalAnalysis;
    if (medical?.available !== true) return [];
    const employerPrice = option.employerPrice ?? option.price;
    return [
        {
            id: 'medical_analysis',
            name: 'Medical Analysis',
            description: medical.label ?? 'Full medical test panel for this check.',
            price: medical.addonPrice,
            priceWithAddon: medical.priceWithMedicalAnalysis ?? employerPrice + medical.addonPrice,
        },
    ];
}

export interface SelectionPricing {
    /** The features being bought, in the plan's order. */
    features: PlanFeatureOption[];
    /** Sum of their standalone prices, before any discount. */
    subtotal: number;
    /** True when every feature is ticked, so the whole-plan discount applies. */
    isWholePlan: boolean;
    discountPercent: number;
    discountAmount: number;
    /** What the features cost: subtotal less any discount. */
    featuresTotal: number;
    /** Selected add-ons, always at face value. */
    addons: CheckAddonOption[];
    addonsTotal: number;
    /** What checkout will charge, before any promo code. */
    total: number;
}

/**
 * Price a selection client-side. Deliberately mirrors the server's
 * `calculateSelection` (POST /payments/quote) so the running total updates on
 * every tick without a round trip.
 *
 * Two rules, both from the server: features are charged at their standalone
 * price, and taking every one of them earns the whole-plan discount. Add-ons
 * never get that discount. Checkout re-prices server-side regardless, so this
 * is a display concern, never the truth.
 */
export function priceSelection(
    option: CheckOption,
    selectedFeatureIds: string[],
    selectedAddonIds: string[] = [],
): SelectionPricing {
    const available = planFeatures(option);
    const picked = new Set(selectedFeatureIds);
    const features = available.filter((f) => picked.has(f.id));
    const subtotal = features.reduce((sum, f) => sum + f.price, 0);

    const isWholePlan = available.length > 0 && features.length === available.length;
    const discountPercent = isWholePlan ? (option.wholePlanDiscountPercent ?? 0) : 0;

    // The whole plan is sold at the employer price, which is the discounted
    // figure and already carries any referral discount; recomputing from the
    // standalone sum would miss that.
    const employerPrice = option.employerPrice ?? option.price;
    const featuresTotal = isWholePlan ? employerPrice : subtotal;

    const addonsPicked = new Set(selectedAddonIds);
    const addons = planAddons(option).filter((a) => addonsPicked.has(a.id));
    const addonsTotal = addons.reduce((sum, a) => sum + a.price, 0);

    return {
        features,
        subtotal,
        isWholePlan,
        discountPercent,
        discountAmount: subtotal - featuresTotal,
        featuresTotal,
        addons,
        addonsTotal,
        total: featuresTotal + addonsTotal,
    };
}
